import {Lang} from 'yougile-lang'
import {logger} from './logger'
import {Util} from './util'

const lz = (s: number): string => ('0' + s).slice(-2)

export interface Duration {
  amount: number
  unit: 'd' | 'm'
}

export interface TimeDiff {
  diff: number
  timeZoneDiff: number
}

const localDiff: TimeDiff = {
  diff: 0,
  timeZoneDiff: 0
}

/**
 * This is very useful when dealing with problem of time zones.
 * This class automatically synchronizes with server and provides with
 * correct server timestamps. The rule of thumbs is to always treat
 * Date type as a local date and communicate with the world with universal
 * timestamps got from this class.
 */
export class Time {
  public static second = 1_000
  public static minute = 60_000
  public static hour = 60 * Time.minute
  public static day = 24 * Time.hour
  public static week = 7 * Time.day
  public static year = 365 * Time.day

  private static updatesHandlerId: any

  public static async initUpdates(
    getDiff: () => Promise<TimeDiff | undefined>
  ): Promise<void> {
    clearInterval(Time.updatesHandlerId)

    const up = async (): Promise<void> => {
      try {
        const res = await getDiff()

        if (res) {
          if (res.diff !== undefined) {
            localDiff.diff = res.diff
          }
          if (res.timeZoneDiff) {
            localDiff.timeZoneDiff = res.timeZoneDiff
          }
        }
      } catch (e) {
        logger.error(e)
      }
    }

    if (!Util.isServer()) {
      await up()
      setTimeout(async () => {
        await up()
        Time.updatesHandlerId = setInterval(up, 600_000)
      }, 30_000)
    }
  }

  public static getTimeDiff(): number {
    return localDiff.diff
  }

  public static setTimeDiff(val: number): void {
    localDiff.diff = val
  }

  public static getTimeZoneDiff(): number {
    return localDiff.timeZoneDiff
  }

  public static setTimeZoneDiff(val: number): void {
    localDiff.timeZoneDiff = val
  }

  public static now(): number {
    return Date.now() - localDiff.diff
  }

  public static toLocalDate(timestamp: number): Date {
    return new Date(+timestamp + localDiff.diff)
  }

  public static toTimestamp(localDate: Date, noDiff = false): number {
    return localDate.getTime() - (noDiff ? 0 : localDiff.diff)
  }

  public static roundToMinutes(timestamp: number): number {
    const d = Time.toLocalDate(timestamp)

    const sec = d.getSeconds()
    const ms = d.getMilliseconds()

    if (sec > 30) {
      return timestamp + (60 - sec) * 1000 - ms
    }

    return timestamp - sec * 1000 - ms
  }

  public static formatShort(
    timestamp: number | undefined,
    round = false
  ): string {
    if (!timestamp) {
      return ''
    }
    let d = Time.toLocalDate(timestamp)

    // rounding to minutes
    if (round) {
      d = Time.toLocalDate(Time.roundToMinutes(timestamp))
    }

    return [d.getHours(), d.getMinutes()].map(lz).join(':')
  }

  public static toStandardString(timestamp: number): string {
    if (!timestamp) {
      return ''
    }
    const p = Time.getParts(timestamp)
    return `${p[0]}-${p[1]}-${p[2]} ${p[3]}:${p[4]}:${p[5]}`
  }

  public static formatLong(timestamp: number): string {
    if (!timestamp) {
      return ''
    }
    const d = Time.toLocalDate(timestamp)
    return (
      [d.getHours(), d.getMinutes(), d.getSeconds()].map(lz).join(':') +
      ' ' +
      d.getDate() +
      ' ' +
      Lang.msg.months[d.getMonth()] +
      ' ' +
      d.getFullYear()
    )
  }

  public static formatDateTime(
    timestamp: number | undefined,
    alwaysWithYear = false,
    skipCurrentDate = false,
    round = false,
    fullMonth = false,
    comma = false,
    skipDate = false
  ): string {
    if (!timestamp) {
      return ''
    }
    const d = Time.toLocalDate(timestamp)
    const today = Time.toLocalDate(Date.now())
    const isCurrentDate =
      today.getFullYear() === d.getFullYear() &&
      today.getMonth() === d.getMonth() &&
      today.getDate() === d.getDate()
    const year =
      alwaysWithYear || d.getFullYear() !== new Date().getFullYear()
        ? ' ' + d.getFullYear()
        : ''

    const months = fullMonth ? Lang.msg.monthsFull : Lang.msg.months

    const needDate = !skipDate && (!isCurrentDate || !skipCurrentDate)
    const date = needDate ? d.getDate() + ' ' + months[d.getMonth()] + year : ''

    const delimiter = date && comma ? ', ' : ' '
    return date + delimiter + Time.formatShort(timestamp, round)
  }

  public static placementDay(date: number, d1: number, d2: number): number {
    if (!date || !d1 || !d2) {
      return -1
    }
    const dateD = Time.toLocalDate(date)
    const d1D = Time.toLocalDate(d1)
    const d2D = Time.toLocalDate(d2)
    if (Time.areDatesEqual(dateD, d1D) || Time.areDatesEqual(dateD, d2D)) {
      return 0
    }
    if (date < d1 && date < d2) {
      return -1
    } else if (date > d1 && date > d2) {
      return 1
    } else {
      return 0
    }
  }

  public static localDayStart(date: Date): Date {
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    return new Date(year, month, day)
  }

  public static extractLocalTimeMs(date: Date): number {
    return date.getTime() - Time.localDayStart(date).getTime()
  }

  public static shiftLocalDate(date: Date, millis: number): Date {
    return Time.toLocalDate(Time.toTimestamp(date) + millis)
  }

  public static formatRange(
    t1: number | undefined,
    t2: number | undefined
  ): string {
    if (!t1 && !t2) {
      return ''
    } else if (!t1) {
      return Time.formatDate(t2)
    } else if (!t2) {
      return Time.formatDate(t1)
    }
    const from = Math.min(t1, t2)
    const to = Math.max(t1, t2)
    const fromD = Time.toLocalDate(from)
    const toD = Time.toLocalDate(to)
    const currYear = new Date().getFullYear()
    const alwaysWithYear =
      fromD.getFullYear() !== currYear || toD.getFullYear() !== currYear
    return (
      Time.formatDate(from, {alwaysWithYear}) +
      ' — ' +
      Time.formatDate(to, {alwaysWithYear})
    )
  }

  public static formatDate(
    timestamp: number | undefined,
    options?: {
      alwaysWithYear?: boolean
      delimiter?: string
      fullMonth?: boolean
    }
  ): string {
    if (!timestamp) {
      return ''
    }
    const {
      alwaysWithYear = false,
      delimiter = '.',
      fullMonth = false
    } = options ?? {}
    const d = Time.toLocalDate(timestamp)

    const sameYear = d.getFullYear() === new Date().getFullYear()
    const showYear = alwaysWithYear || !sameYear

    if (!fullMonth && showYear) {
      return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
        .map(lz)
        .join(delimiter)
    }

    const months = fullMonth ? Lang.msg.monthsFull : Lang.msg.months
    const yearPart = showYear ? ` ${d.getFullYear()}` : ''
    return `${d.getDate()} ${months[d.getMonth()]}${yearPart}`
  }

  public static getParts(timestamp: number): string[] {
    if (!timestamp) {
      return []
    }
    const d = Time.toLocalDate(timestamp)
    const res = [
      0,
      d.getMonth() + 1,
      d.getDate(),
      d.getHours(),
      d.getMinutes(),
      d.getSeconds()
    ].map(lz)
    res.splice(0, 1, d.getFullYear() + '')
    return res
  }

  public static addDays(date: Date, days: number): Date {
    const result = new Date(date)
    result.setDate(result.getDate() + days)
    return result
  }

  public static toDateStr(timestamp: number): string {
    if (!timestamp) {
      return ''
    }
    const parts = Time.getParts(timestamp)
    return parts.slice(0, 3).join('-')
  }

  public static fromDateStr(dateStr: string): number | undefined {
    if (!dateStr) {
      return undefined
    }
    const parts = dateStr
      .split('-')
      .map(n => +n)
      .filter(n => !!n)
    if (parts.length !== 3) {
      return undefined
    }
    const d = new Date()
    d.setFullYear(parts[0])
    d.setMonth(parts[1] - 1)
    d.setDate(parts[2])
    d.setHours(0)
    d.setMinutes(0)
    d.setSeconds(0)
    d.setMilliseconds(0)
    return d.getTime()
  }

  /**
   * Returns beginning of day in local timezone
   */
  public static startOfDay(timestamp: number): number {
    const local = Time.toLocalDate(timestamp)
    local.setHours(0)
    local.setMinutes(0)
    local.setSeconds(0)
    local.setMilliseconds(0)
    return Time.toTimestamp(local)
  }

  public static startOfDayOnServer(timestamp: number): number {
    const local = Time.toLocalDate(timestamp)
    local.setHours(0)
    local.setMinutes(0)
    local.setSeconds(0)
    local.setMilliseconds(0)
    return Time.toTimestamp(local) + Time.getTimeDiff()
  }

  /**
   * Returns end of day in local timezone
   */
  public static endOfDay(timestamp: number): number {
    return Time.startOfDay(timestamp + Time.day)
  }

  public static middleOfDay(timestamp: number): number {
    return Time.startOfDay(timestamp) + 12 * Time.hour
  }

  public static startOfWeek(timestamp: number): number {
    const local = Time.toLocalDate(timestamp)
    const dayOfWeek = local.getDay()
    const startOfDay = Time.startOfDay(timestamp)
    if (dayOfWeek === 0) {
      return startOfDay - 6 * Time.day
    } else {
      return startOfDay - (dayOfWeek - 1) * Time.day
    }
  }

  public static startOfMonth(timestamp: number): number {
    const local = Time.toLocalDate(timestamp)
    local.setDate(1)
    local.setHours(0)
    local.setMinutes(0)
    local.setSeconds(0)
    local.setMilliseconds(0)
    return Time.toTimestamp(local)
  }

  public static endOfMonth(timestamp: number): number {
    const local = Time.toLocalDate(timestamp)
    local.setMonth(local.getMonth() + 1)
    return Time.startOfMonth(Time.toTimestamp(local))
  }

  public static startOfYear(timestamp: number): number {
    const local = Time.toLocalDate(timestamp)
    local.setMonth(0)
    local.setDate(1)
    local.setHours(0)
    local.setMinutes(0)
    local.setSeconds(0)
    local.setMilliseconds(0)
    return Time.toTimestamp(local)
  }

  public static clone(date: Date): Date {
    const t = Time.toTimestamp(date)
    return Time.toLocalDate(t)
  }

  public static areDatesEqual(d1: Date, d2: Date): boolean {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    )
  }

  public static sameDates(t1: number, t2: number): boolean {
    return Time.areDatesEqual(Time.toLocalDate(t1), Time.toLocalDate(t2))
  }

  public static dayBetween(day: number, from: number, to: number): boolean {
    const dayD = Time.toLocalDate(day)
    const fromD = Time.toLocalDate(from)
    const toD = Time.toLocalDate(to)
    return (
      (day >= from && day <= to) ||
      Time.areDatesEqual(dayD, fromD) ||
      Time.areDatesEqual(dayD, toD)
    )
  }

  public static formatCustom(
    timestamp: number | undefined,
    format: string
  ): string {
    if (!timestamp) {
      return ''
    }
    const date = Time.toLocalDate(timestamp)

    return format
      .replace('DD', lz(date.getDate()))
      .replace('MM', lz(date.getMonth() + 1))
      .replace('YYYY', `${date.getFullYear()}`)
      .replace('YY', lz(date.getFullYear() % 100))
      .replace('hh', lz(date.getHours()))
      .replace('mm', lz(date.getMinutes()))
      .replace('ss', lz(date.getSeconds()))
  }

  public static timestampAsText(
    timestamp: number,
    todayFormat: string,
    yesterdayFormat: string
  ): string {
    const date = new Date(timestamp)
    const nextDay = new Date(timestamp + Time.day)
    const today = new Date()

    if (Time.areDatesEqual(date, today)) {
      return todayFormat
        .replace('hh', `${date.getHours()}`)
        .replace('mm', lz(date.getMinutes()))
    }

    if (Time.areDatesEqual(nextDay, today)) {
      return yesterdayFormat
        .replace('hh', `${date.getHours()}`)
        .replace('mm', lz(date.getMinutes()))
    }

    return Time.formatDateTime(timestamp, false, false, false, false, true)
  }

  public static readableTimestamp(timestamp: number): string {
    return timestamp
      ? Time.timestampAsText(
          timestamp,
          Lang.msg.todayFormat,
          Lang.msg.yesterdayFormat
        )
      : ''
  }

  /**
   * Example: 2018-10-24 18:15:57 (Wed)
   */
  public static timestampToReadable(timestamp: number): string {
    const p = Time.getParts(timestamp)
    const d = Time.toLocalDate(timestamp)
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return (
      p.slice(0, 3).join('.') +
      ' ' +
      p.slice(3).join(':') +
      ` (${days[d.getDay()]})`
    )
  }

  public static addDuration(timestamp: number, duration: Duration): number {
    if (!duration) {
      return timestamp
    }
    switch (duration.unit) {
      case 'd':
        return timestamp + duration.amount * Time.day
      case 'm': {
        const d = new Date(timestamp)
        d.setMonth(d.getMonth() + duration.amount)
        return d.getTime()
      }
      default:
        logger.warn('Incorrect duration', duration)
        return timestamp
    }
  }

  /**
   * Counts weekend days (Saturdays and Sundays) between two dates.
   *
   * See https://stackoverflow.com/questions/6210906/how-to-determine-number-saturdays-and-sundays-comes-between-two-dates-in-java-sc#comment32570764_21465288
   */
  public static weekendsBetween(t0: number, t1: number): number {
    const ndays = 1 + Math.floor((t1 - t0) / Time.day)
    const d0 = new Date(t0)
    const d1 = new Date(t1)
    const nsundays = Math.floor((ndays + ((d0.getDay() + 6) % 7)) / 7)
    return 2 * nsundays + Number(d1.getDay() === 6) - Number(d0.getDay() === 0)
  }

  public static workingDaysBetween(t0: number, t1: number): number {
    const calendarDays = 1 + Math.floor((t1 - t0) / Time.day)
    return calendarDays - Time.weekendsBetween(t0, t1)
  }

  /**
   * Same as `workingDaysBetween()`, but if a task starts and ends on the same
   * weekend, then outputs 1 day.
   */
  public static weekendAdjustedWorkingDaysBetween(
    t0: number,
    t1: number
  ): number {
    const calendarDays = 1 + Math.floor((t1 - t0) / Time.day)
    const weekendDays = Time.weekendsBetween(t0, t1)
    return calendarDays <= weekendDays ? 1 : calendarDays - weekendDays
  }

  public static applyTimeOffset(date: Date, reference: Date): void {
    date.setHours(reference.getHours())
    date.setMinutes(reference.getMinutes())
    date.setSeconds(reference.getSeconds())
    date.setMilliseconds(reference.getMilliseconds())
  }

  /**
   * If true, this means that start and end dates may be on the same day:
   *   2024-02-14 12-00-00-000 (start)
   *   2024-02-16 16-00-00-000 (end)
   *   2024-02-14 16-00-00-000 (end, changed)
   *
   * If false, this means that start and end dates must be at least a day apart:
   *   2024-02-14 16-00-00-000 (start)
   *   2024-02-15 12-00-00-000 (end)
   *   2024-02-16 12-00-00-000 (end, changed, less than start date)
   *   2024-02-15 12-00-00-000 (end, fixed)
   **/
  public static canTimeBeOnSameDate(left: number, right: number): boolean {
    const leftDate = new Date(left)
    const rightDate = new Date(right)
    rightDate.setFullYear(
      leftDate.getFullYear(),
      leftDate.getMonth(),
      leftDate.getDate()
    )
    return leftDate.getTime() <= rightDate.getTime()
  }

  public static addWorkingDays(date: Date, workingDays: number): Date {
    if (!Number.isSafeInteger(workingDays)) {
      throw new Error('workingDays must be an integer')
    }

    let result = new Date(date)

    if (workingDays === 0) {
      return result
    } else if (workingDays > 0) {
      // If already on a weekend shift to Mon
      result = Time.normalizeToWeekday(result, true)

      const daysUntilWeekend = 6 - result.getDay() // Sat minus current weekday
      if (workingDays < daysUntilWeekend) {
        // Stays on the same week, no need for calculations
        result.setDate(result.getDate() + workingDays)
        return result
      }

      // For each 5 work days add 2 weekends
      const addedDays = 2 + 2 * Math.floor((workingDays - daysUntilWeekend) / 5)
      result.setDate(result.getDate() + workingDays + addedDays)
      return result
    } else {
      workingDays = Math.abs(workingDays) // workingDays < 0

      // If already on a weekend shift to Fri
      result = Time.normalizeToWeekday(result, false)

      const daysUntilWeekend = result.getDay() // current weekday
      if (workingDays <= daysUntilWeekend) {
        // Stays on the same week, no need for calculations
        result.setDate(result.getDate() - workingDays)
        return result
      }

      // For each 5 work days remove 2 weekends
      const removedDays =
        2 + 2 * Math.floor((workingDays - daysUntilWeekend) / 5)
      result.setDate(result.getDate() - workingDays - removedDays)
      return result
    }
  }

  /**
   * If a `date` is a weekend
   * - shifts it to next Monday if `forward` is `true`,
   * - or to previous Friday if `forward` is `false`,
   *
   * else returns the same date
   */
  public static normalizeToWeekday(date: Date, forward: boolean): Date {
    const result = new Date(date)
    if (forward) {
      // Shift from weekends to Mon
      if (result.getDay() === 0) {
        result.setDate(result.getDate() + 1) // Shift Sun to Mon
      } else if (result.getDay() === 6) {
        result.setDate(result.getDate() + 2) // Shift Sat to Mon
      }
    } else {
      // Shift from weekends to Fri
      if (result.getDay() === 0) {
        result.setDate(result.getDate() - 2) // Shift Sun to Fri
      } else if (result.getDay() === 6) {
        result.setDate(result.getDate() - 1) // Shift Sat to Fri
      }
    }
    return result
  }

  public static isSameDate(left: number | Date, right: number | Date): boolean {
    const leftDate = new Date(left)
    const rightDate = new Date(right)
    return (
      leftDate.getFullYear() === rightDate.getFullYear() &&
      leftDate.getMonth() === rightDate.getMonth() &&
      leftDate.getDate() === rightDate.getDate()
    )
  }

  public static isWeekend(date: number | Date): boolean {
    const actualDate = new Date(date)
    return actualDate.getDay() === 6 || actualDate.getDay() === 0
  }
}
